import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {RelevantItemsGraphQL} from 'relevant-items/__generated/RelevantItemsGraphQL';
import ExploreItemViaAIChatButton from 'relevant-items/ExploreItemViaAIChatButton';
import getTestProps from 'test/lib/cypress/getTestProps';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import PlainSearchInput, {ClearType} from 'ui-library/PlainSearchInput';
import {GenericEvent} from 'util/Types';

import RelevantItemsSectionTable from './RelevantItemsSectionTable';

export enum RelevantItemsFilterOption {
  ALL = 'All',
  BULK_ASSEMBLIES = 'Bulk Assemblies',
  ENTRIES = 'Entries',
  ENTITY_LINKS = 'Entity links',
  SEQUENCE_PRIMERS = 'Primer binding sites',
  LEGACY_WORKFLOWS = 'Legacy Workflows',
  PROCESSES = 'Task groups',
  RUNS = 'Runs',
  WORKSHEET_STEPS = 'Worksheet steps',
}

interface RelevantItemsSectionHeaderProps {
  filteredTableState: RelevantItemsFilteredTableState;
  setFilteredTableState: (state: RelevantItemsFilteredTableState) => void;
  fileId: string;
}

function RelevantItemsSectionHeader(props: RelevantItemsSectionHeaderProps): JSX.Element {
  const {filterOption, searchQuery} = props.filteredTableState;
  const filteredMenuOptions = Object.values(RelevantItemsFilterOption)
    .filter(
      (option) =>
        option !== RelevantItemsFilterOption.LEGACY_WORKFLOWS || BenchlingConfig.LEGACY_WORKFLOWS_ENABLE
    )
    .filter((option) => option !== RelevantItemsFilterOption.PROCESSES || BenchlingConfig.WORKFLOWS_ENABLE);
  return (
    <div className={classNames('pull-left', css(styles.header))}>
      <div className="u-lr-split u-small-spacing-bottom">
        <div className={css(styles.leftControls)}>
          <SelectDropdown
            className="u-display-inline-block"
            displayName={filterOptionToDisplayName(filterOption)}
            menu={
              <Menu width={MenuWidth.MEDIUM}>
                {filteredMenuOptions.map((option) => (
                  <Option
                    key={option}
                    onSelect={() => {
                      props.setFilteredTableState({filterOption: option, searchQuery, pageOffset: 0});
                    }}
                  >
                    {filterOptionToDisplayName(option)}
                  </Option>
                ))}
              </Menu>
            }
          />
        </div>
        <div className={css(styles.rightControls)}>
          <ExploreItemViaAIChatButton fileId={props.fileId} />
          <PlainSearchInput
            clearType={ClearType.X}
            placeholder="Search"
            onChange={(e: GenericEvent) => {
              props.setFilteredTableState({
                filterOption,
                searchQuery: e.target.value,
                pageOffset: 0,
              });
            }}
            onEscape={() => {
              props.setFilteredTableState({
                filterOption,
                searchQuery: '',
                pageOffset: 0,
              });
            }}
            value={searchQuery}
          />
        </div>
      </div>
    </div>
  );
}

function filterOptionToDisplayName(
  filterOption: RelevantItemsFilterOption
): RelevantItemsFilterOption | string {
  switch (filterOption) {
    case RelevantItemsFilterOption.LEGACY_WORKFLOWS:
      return BenchlingConfig.WORKFLOWS_ENABLE ? 'Legacy Workflows' : 'Workflows';
    default:
      return filterOption;
  }
}

export interface RelevantItemsFilteredTableState {
  searchQuery: string;
  filterOption: RelevantItemsFilterOption;
  pageOffset: number;
}

export const relevantItemsFilteredTableState = makeUseGlobalClientState<RelevantItemsFilteredTableState>({
  searchQuery: '',
  filterOption: RelevantItemsFilterOption.ALL,
  pageOffset: 0,
});

export interface PresentationalRelevantItemsSectionProps {
  relevantItemsGraphQL: RelevantItemsGraphQL;
  itemId: string;
  relevantConvertedFromItemsGraphQL: RelevantItemsGraphQL | null;
  /**
   * Makes the table rows/columns more compact by eliminating extra whitespace.
   *
   * @default false
   */
  isCompact?: boolean;
  /** @default 100 */
  paginationLimit?: number;
}

export default function PresentationalRelevantItemsSection({
  relevantItemsGraphQL,
  itemId,
  relevantConvertedFromItemsGraphQL,
  isCompact,
  paginationLimit,
}: PresentationalRelevantItemsSectionProps): JSX.Element | null {
  const [filteredTableState, setFilteredTableState] =
    relevantItemsFilteredTableState.useGlobalClientState(itemId);
  return (
    <div {...getTestProps({component: 'PresentationalRelevantItemsSection', element: 'root'})}>
      <RelevantItemsSectionHeader
        filteredTableState={filteredTableState}
        setFilteredTableState={setFilteredTableState}
        fileId={itemId}
      />

      <RelevantItemsSectionTable
        relevantItemsGraphQL={relevantItemsGraphQL}
        relevantConvertedFromItemsGraphQL={relevantConvertedFromItemsGraphQL}
        filteredTableState={filteredTableState}
        setFilteredTableState={setFilteredTableState}
        itemId={itemId}
        isCompact={isCompact}
        paginationLimit={paginationLimit}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  header: {
    lineHeight: '24px' /* Match button height */,
    width: '100%',
    marginLeft: 3 /* Otherwise header text is misaligned with above table */,
  },
  leftControls: {
    display: 'flex',
    alignItems: 'center',
  },
  rightControls: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
});
